import { Box, Radio, RadioGroup, FormControlLabel, Stack, Typography } from '@mui/material';
import { useGetA2pCampaignPricing } from '@/apis/business/setting/phoneNumbers/hook';
import { formatDollars } from '@/utils/helperFunctions';
import { MdInfoOutline } from 'react-icons/md';
import { BORDER_COLOR, DEFAULT_USECASES, StepFormProps } from './campaignFormShared';

type Props = StepFormProps;

const CARD_SX = {
  border: `1px solid ${BORDER_COLOR}`,
  borderRadius: '8px',
  px: 2,
  py: 2,
  cursor: 'pointer'
} as const;

export default function CampaignTypeStep({ watch, setValue, lockType }: Props) {
  const campaignType = watch('campaign_type');
  const { data } = useGetA2pCampaignPricing();
  const pricing = data?.data;
  const lowVolumePrice = formatDollars(pricing?.low_volume_campaign_per_month ?? 3);
  const mixedPrice = formatDollars(pricing?.mixed_campaign_per_month ?? 20);

  const selectType = (value: 'LOW_VOLUME' | 'MIXED') => {
    if (lockType) return;
    setValue('campaign_type', value, { shouldDirty: true });
    setValue('usecases', DEFAULT_USECASES, { shouldDirty: true });
  };

  return (
    <Box>
      <Box bgcolor='#FFF8E6' borderRadius='8px' px={2} py={1.5} mb={2.5}>
        <Stack direction='row' spacing={1} alignItems='flex-start'>
          <Box color='#D97706' mt={0.15}>
            <MdInfoOutline size={18} />
          </Box>
          <Box>
            <Typography fontSize={13} fontWeight={600} color='#D97706' mb={0.5}>
              NOTE:
            </Typography>
            <Box component='ul' sx={{ m: 0, pl: 2, color: '#D97706' }}>
              <Typography component='li' fontSize={13} lineHeight={1.6} color='#D97706'>
                Each campaign requires an initial 3-month commitment. If you cancel before the 3-month commitment ends,
                you will continue to be charged for the remaining commitment period.
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Box>

      <RadioGroup value={campaignType} onChange={(_, value) => selectType(value as 'LOW_VOLUME' | 'MIXED')}>
        <Box
          {...CARD_SX}
          mb={1.5}
          onClick={() => selectType('LOW_VOLUME')}
          border={campaignType === 'LOW_VOLUME' ? '1px solid #2563EB' : `1px solid ${BORDER_COLOR}`}
        >
          <Stack direction='row' justifyContent='space-between' alignItems='flex-start' mb={0.75}>
            <FormControlLabel
              value='LOW_VOLUME'
              disabled={lockType}
              control={<Radio size='small' />}
              label={
                <Typography fontSize={15} fontWeight={600}>
                  Low Volume Mixed
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Typography fontSize={13} fontWeight={600} color='#2563EB'>
              {lowVolumePrice}/mo
            </Typography>
          </Stack>
          <Typography fontSize={13} color='text.primary' lineHeight={1.6} pl={4}>
            Low Volume campaigns suit small businesses sending under 15,000 messages monthly. They are ideal for
            non-time-sensitive messages and offer a cost-effective option for less volume-heavy use cases.
          </Typography>
          <Typography fontSize={13} color='text.primary' lineHeight={1.6} pl={4} mt={1}>
            <Box component='span' fontWeight={600}>
              Note:{' '}
            </Box>
            You can send up to 2,000 messages a day per brand to T-Mobile, but you&apos;re limited to 75 SMS message
            segments a minute on the AT&T network. You cannot use vetting to get a higher throughput on these types of
            campaigns.
          </Typography>
        </Box>

        <Box
          {...CARD_SX}
          onClick={() => selectType('MIXED')}
          border={campaignType === 'MIXED' ? '1px solid #2563EB' : `1px solid ${BORDER_COLOR}`}
        >
          <Stack direction='row' justifyContent='space-between' alignItems='flex-start' mb={0.75}>
            <FormControlLabel
              value='MIXED'
              disabled={lockType}
              control={<Radio size='small' />}
              label={
                <Typography fontSize={15} fontWeight={600}>
                  Mixed
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Typography fontSize={13} fontWeight={600} color='#2563EB'>
              {mixedPrice}/mo
            </Typography>
          </Stack>
          <Typography fontSize={13} color='text.primary' lineHeight={1.6} pl={4}>
            Any messaging campaign containing 2 to 5 standard use cases.
          </Typography>
        </Box>
      </RadioGroup>
    </Box>
  );
}
